import React, { useState, useEffect } from 'react';

function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);

  useEffect(() => {
    // Detect iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if app is already installed (in standalone mode)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                                window.navigator.standalone === true;

    // Check if the banner has been dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    setHasBeenDismissed(!!dismissed);

    // Show install banner if not installed, not dismissed, and not on iOS
    if (!isInStandaloneMode && !isIOSDevice && !dismissed) {
      // Show banner after 3 seconds of user interaction
      const timer = setTimeout(() => {
        setShowInstallBanner(true);
      }, 3000);

      return () => clearTimeout(timer);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner only if not dismissed
      if (!localStorage.getItem('pwa-install-dismissed')) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setShowInstallBanner(false);
      
      // Clear dismissed flag when user installs
      localStorage.removeItem('pwa-install-dismissed');
    }
  };

  const handleIOSInstall = () => {
    // Instructions for iOS installation
    const instructions = `
Para instalar esta aplicación en tu dispositivo iOS:

1. Toca el botón para compartir (📤) en la parte inferior de Safari
2. Desplázate hacia abajo y selecciona "Añadir a pantalla de inicio"
3. Toca "Añadir" para confirmar
4. La aplicación aparecerá en tu pantalla de inicio

¡Listo! Podrás acceder a la aplicación directamente desde tu pantalla de inicio.
    `;
    alert(instructions);
    handleDismiss();
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    setHasBeenDismissed(true);
  };

  const handleReenable = () => {
    localStorage.removeItem('pwa-install-dismissed');
    setHasBeenDismissed(false);
    if (deferredPrompt) {
      setShowInstallBanner(true);
    }
  };

  // Don't show anything if no prompt available or already dismissed
  if (!showInstallBanner || !deferredPrompt) {
    // Show re-enable option only if user has dismissed before and there's a prompt available
    if (hasBeenDismissed && deferredPrompt) {
      return (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            onClick={handleReenable}
            className="text-xs text-gray-500 hover:text-gray-700 bg-white px-2 py-1 rounded shadow-md border border-gray-200 transition-colors"
            title="Mostrar opción de instalación"
          >
            📱 Instalar app
          </button>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform transition-transform duration-300 ease-in-out">
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <img 
                src="https://assets.brandbay.io/brands/1736547941865x394131539339247600/assets/OX3X76ITURRI_YourSafetyMatters.jpg" 
                alt="ITURRI Tallaje" 
                className="w-10 h-10 rounded-lg object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium">Instalar ITURRI Tallaje</p>
              <p className="text-xs text-blue-100">Instala la aplicación para un acceso más rápido y mejor experiencia</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={deferredPrompt ? handleInstallClick : handleIOSInstall}
              className="px-4 py-2 bg-white text-blue-600 rounded-md font-medium text-sm hover:bg-blue-50 transition-colors"
            >
              {isIOS ? 'Cómo instalar' : 'Instalar ahora'}
            </button>
            
            <button
              onClick={handleDismiss}
              className="p-2 text-blue-100 hover:text-white hover:bg-blue-800 rounded-md transition-colors"
              title="No, gracias"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;